import { useParentIframe } from "@trello-powerup-monorepo/iframe-messenger-v2";
import React, { useContext, useCallback, createContext } from "react";
import type { PaywallMessageMeta } from "../../types";
import { PAYWALL_MESSAGE_TYPES } from "../../constants";
import { IframeInitializationData } from "../../types";
import { usePaywallActions } from "../hooks";
import { isPaywallMessage } from "../../logic";

export interface PaywallIframeManagerContextValue {
  // EXISTING - Semantic navigation (unchanged for backward compatibility)
  showDashboard: () => void;
  showCreateProfile: () => void;
  showPricing: () => void;
  hide: () => void;

  // EXISTING - Infrastructure state (unchanged for backward compatibility)
  isReady: boolean;

  // // EXISTING - Security state (unchanged for backward compatibility)
  // securityStep: SecurityStep;

  // EXISTING - Initialization (unchanged for backward compatibility)
  sendIframeInitialization: (data: IframeInitializationData) => void;

  // EXISTING - Events (unchanged for backward compatibility)
  onProfileCreated: (handler: () => void) => () => void;
}

// Types
interface PaywallIframeManagerProps {
  appId: string;
  onSessionId?: (sessionId: string) => void;
}

const PaywallIframeManagerContext =
  createContext<PaywallIframeManagerContextValue | null>(null);

export function usePaywallIframeManager(): PaywallIframeManagerContextValue {
  const context = useContext(PaywallIframeManagerContext);
  if (!context) {
    throw new Error(
      "usePaywallIframeManager must be used within PaywallIframeManagerProvider"
    );
  }
  return context;
}

export function PaywallIframeManagerProvider({
  children,
  appId,
  onSessionId,
}: React.PropsWithChildren<PaywallIframeManagerProps>) {
  const { setVisibility, onMessage, isReady, sendMessage } = useParentIframe();

  // AIDEV-NOTE: Stable session id for correlation during the paywall lifecycle
  const sessionIdRef = React.useRef<string>(generateSessionId());

  // AIDEV-NOTE: Expose sessionId to host app for tagging (Sentry/PostHog)
  React.useEffect(() => {
    if (onSessionId) onSessionId(sessionIdRef.current);
  }, [onSessionId]);

  const getMeta = React.useCallback(
    (): PaywallMessageMeta => ({
      source: appId,
      sessionId: sessionIdRef.current,
    }),
    [appId]
  );

  const paywallActions = usePaywallActions(
    sendMessage,
    setVisibility,
    appId,
    getMeta
  );

  // AIDEV-NOTE: sessionId available via getMeta(); dev-only log removed after verification

  const onProfileCreated = useCallback(
    (handler: () => void) => {
      return onMessage((message) => {
        if (
          isPaywallMessage(message) &&
          message.type === PAYWALL_MESSAGE_TYPES.PROFILE_CREATED
        ) {
          handler();
        }
      });
    },
    [onMessage]
  );

  // Basic context without unified state (will be enhanced by PaywallStateManager)
  const contextValue: PaywallIframeManagerContextValue = React.useMemo(
    () => ({
      ...paywallActions,
      isReady,
      onProfileCreated,
      // securityStep: "checking-auth",
      // authStatus: "unauthenticated",
      // isAuthenticated: false,
      // subscriptions: [],
      // resources: [],
    }),
    [paywallActions, isReady, onProfileCreated]
  );

  return (
    <PaywallIframeManagerContext.Provider value={contextValue}>
      {children}
    </PaywallIframeManagerContext.Provider>
  );
}

// AIDEV-NOTE: Private helper; parent-only session id generator (KISS/YAGNI)
function generateSessionId(): string {
  try {
    // @ts-ignore
    return typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  } catch {
    return Math.random().toString(36).slice(2);
  }
}
